import Decimal from 'decimal.js';
import { compact, first } from 'lodash';

import { AIOnboardingLiabilityField } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/AIOnboardingModalForm.types';
import { getHoldingOwnerEntityID } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/fields/holdings.utils';
import { AiOnboardingModal_HouseholdFragment } from '@/modules/aiOnboarding/AIOnboardingModal/graphql/aiOnboardingModal.generated';
import { AiSuggestionForReviewCard } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import {
  getKgV2NodeIDFromSuggestion,
  LIABILITY_CREDITOR_PREDICATE,
} from '@/modules/aiSuggestions/kgV2Resolution.utils';
import { mapFormDataToInput } from '@/modules/liabilities/LiabilityModal/CreateLiabilityModal.utils';
import {
  defaultValues,
  NO_LIABILITY_SOURCE_RECIPIENT_VALUE_SENTINEL,
} from '@/modules/liabilities/LiabilityModal/LiabilityModal.constants';
import { AugmentedCreateLiabilityInput } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

///////////////////////////////////////////////////////////
// SUGGESTION DATA -> FORM DATA
///////////////////////////////////////////////////////////

function getLiabilityCreditorEntityID(
  suggestion: AiSuggestionForReviewCard
): string | undefined {
  const creditorStatement = getNodes(suggestion.kgV2Statements).find(
    (statement) => statement.predicate === LIABILITY_CREDITOR_PREDICATE
  );

  return first(getNodes(creditorStatement?.subject.resolvedEntities))?.id;
}

/**
 * Maps AI suggestions of kind LIABILITY to form data structure for the AIOnboarding
 * modal form.
 *
 * V2 suggestions use the explicit Entity edge for the debtor. The creditor is
 * a separate relationship, so it is resolved from the minimal statement data
 * retained by callers that support direct creation.
 *
 * @param suggestions
 * @param household
 */
export function mapLiabilitiesToFormData(
  suggestions: AiSuggestionForReviewCard[],
  household: AiOnboardingModal_HouseholdFragment
): AIOnboardingLiabilityField[] {
  const entities = getNodes(household.entities);

  return compact(
    suggestions.map((s) => {
      if (!s.liability) return null;

      // Parse outstanding balance to Decimal if it exists
      let outstandingBalance = null;
      if (s.liability.outstandingBalance) {
        try {
          outstandingBalance = new Decimal(s.liability.outstandingBalance);
        } catch {
          // If parsing fails, leave as null
          outstandingBalance = null;
        }
      }

      // Parse as of date if it exists
      let balanceAsOfDate: Date | null = new Date();
      if (s.liability.asOfDate) {
        try {
          balanceAsOfDate = new Date(s.liability.asOfDate);
        } catch {
          // If parsing fails, use current date
          balanceAsOfDate = new Date();
        }
      }

      let recipientId: string;
      let issuerId: string;

      if (s.entity?.id) {
        const creditorId = getLiabilityCreditorEntityID(s);

        recipientId = s.entity.id;
        issuerId = creditorId || NO_LIABILITY_SOURCE_RECIPIENT_VALUE_SENTINEL;
      } else {
        recipientId =
          getHoldingOwnerEntityID(s.liability.debtorEntityID || '', entities) ||
          NO_LIABILITY_SOURCE_RECIPIENT_VALUE_SENTINEL;
        issuerId =
          getHoldingOwnerEntityID(s.liability.lenderEntityID || '', entities) ||
          NO_LIABILITY_SOURCE_RECIPIENT_VALUE_SENTINEL;
      }

      return {
        _suggestionID: s.id,
        // Required LiabilityFormShape fields
        _showExpandedPaymentDetails: false,
        name: s.liability.liabilityName || '',
        recipientId,
        issuerId,
        outstandingBalance,
        balanceAsOfDate,
        paymentFrequency: defaultValues.paymentFrequency,
        interestRate: null,
        termStartDate: null,
        termEndDate: null,
        paymentKind: defaultValues.paymentKind,
        paymentAmount: null,
        notes: '',
        hasDifferentPaymentSource: false,
        alternateSourceId: null,
        integrationLiabilityId: null,
      };
    })
  );
}

export function getEmptyLiabilityField(): AIOnboardingLiabilityField {
  return {
    _suggestionID: undefined,
    ...defaultValues,
  };
}

///////////////////////////////////////////////////////////
// FORM DATA -> MUTATION INPUT
///////////////////////////////////////////////////////////

/**
 * Maps the liability fields from the form data to the input structure for creating liabilities.
 * Returns an array of liability create inputs.
 *
 * @param liabilities
 * @param household
 * @param suggestionsByID
 */
export function mapLiabilitiesToInput(
  liabilities: AIOnboardingLiabilityField[],
  household: AiOnboardingModal_HouseholdFragment,
  suggestionsByID: Record<string, AiSuggestionForReviewCard>
): AugmentedCreateLiabilityInput[] {
  return liabilities.map((liability) => {
    const suggestion = liability._suggestionID
      ? suggestionsByID[liability._suggestionID]
      : undefined;
    const kgV2NodeID = getKgV2NodeIDFromSuggestion(suggestion);

    const baseInput = mapFormDataToInput(liability, household.id);

    return {
      ...baseInput,
      create: {
        ...baseInput.create,
        kgV2NodeIDs: kgV2NodeID ? [kgV2NodeID] : undefined,
      },
    };
  });
}
